import { API_STATUS_MAP, ApiTicketItem, Ticket, TicketCategory } from "./support.types";

// Shared ticket-listing mappers. These live outside ViewTickets because the Lodge a Complaint
// flow also needs them: to open a ticket's details page it must resolve the listing's req_id,
// which the verify-ticket response doesn't carry.

// An unset due date comes back from the CRM as null, and `new Date(null)` is the Unix epoch —
// which is how "1 Jan 1970" ended up on ticket cards. Anything missing, unparseable, or dated
// 1970 or earlier is treated as "no date" and returned blank, so display sites can show their
// own placeholder instead of a date no customer can act on.
export const formatApiDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.getFullYear() <= 1970) return "";
  return (
    d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    ", " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }).toLowerCase()
  );
};

export const mapCategory = (cat: string | null): TicketCategory => {
  if (!cat) return "Query";
  const lower = cat.toLowerCase();
  if (lower.includes("complaint")) return "Complaint";
  if (lower.includes("request")) return "Request";
  return "Query";
};

// Straight-through mapping — each listing field keeps its own name. `typeOfTicket` is the
// descriptive type ("Fees and Charges") shown under the card's "Type of Ticket" label, and
// `category` is the Complaint / Request / Query bucket shown under "Category". Either can come
// back null, so both display sites fall back rather than assuming a value is present.
export const mapApiTicket = (t: ApiTicketItem): Ticket => ({
  id: t.ticketNumber.toString(),
  subject: t.escalatedFrom || t.typeOfTicket || t.category || "",
  category: mapCategory(t.category),
  subCategory: t.category ?? undefined,
  ticketNumber: t.ticketNumber.toString(),
  loanAccountNumber: t.lan,
  status: API_STATUS_MAP[t.status] ?? "In-Progress",
  dateTime: formatApiDate(t.dateAndTime),
  dueDate: formatApiDate(t.dueDate),
  raisedOn: formatApiDate(t.dateAndTime),
  raisedOnIso: t.dateAndTime,
  referenceId: t.req_id.toString(),
  escalatedFrom: t.escalatedFrom,
  escalatedTo: t.escalatedTo,
  type: t.type,
  typeOfTicket: t.typeOfTicket ?? undefined,
});
